/**
 * ConvertDevisModal
 * Modal for converting a devis to affaire products
 * User provides an affaire ID, system copies devis lines to that affaire
 */

import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useConvertDevis } from '../../hooks/useDevis';
import type { Devis } from '../../types/facturation.types';
import '../../facturation.css';

interface ConvertDevisModalProps {
  isOpen: boolean;
  onClose: () => void;
  devis: Devis;
  onSuccess?: () => void;
}

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

export const ConvertDevisModal: React.FC<ConvertDevisModalProps> = ({
  isOpen,
  onClose,
  devis,
  onSuccess,
}) => {
  const [affaireId, setAffaireId] = useState('');
  const [step, setStep] = useState<'input' | 'confirm' | 'converting' | 'success' | 'error'>('input');
  const [errorMessage, setErrorMessage] = useState('');

  const convertMutation = useConvertDevis();

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setAffaireId('');
      setStep('input');
      setErrorMessage('');
    }
  }, [isOpen]);

  const handleProceedToConfirm = () => {
    if (!affaireId.trim()) {
      setErrorMessage('Veuillez entrer un numero d\'affaire');
      return;
    }
    setStep('confirm');
  };

  const handleConvert = async () => {
    setStep('converting');

    try {
      await convertMutation.mutateAsync({
        devisId: devis.id,
        affaireId: affaireId.trim(),
      });
      setStep('success');
      onSuccess?.();
    } catch (error: unknown) {
      setStep('error');
      setErrorMessage(error instanceof Error ? error.message : 'Une erreur est survenue');
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        onClose={onClose}
        className="relative z-50"
        // Focus trap is automatically enabled by HeadlessUI Dialog
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header - Focus trap is automatically handled by HeadlessUI Dialog */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ArrowPathIcon className="h-6 w-6 text-blue-600" />
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Convertir le devis
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">
                        {devis.devis_number} - {devis.contact_name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="facturation-btn-modal-close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Input step */}
                  {step === 'input' && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          Cette action copiera les produits du devis vers une affaire existante.
                          Les produits seront ajoutes a la liste des produits facturables de l'affaire.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Numero d'affaire *
                        </label>
                        <input
                          type="text"
                          value={affaireId}
                          onChange={(e) => {
                            setAffaireId(e.target.value);
                            setErrorMessage('');
                          }}
                          placeholder="Ex: 69408"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg"
                          autoFocus
                        />
                        {errorMessage && step === 'input' && (
                          <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                        )}
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Resume du devis</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div className="flex justify-between">
                            <span>Lignes:</span>
                            <span>{devis.lines?.length || 0} produit(s)</span>
                          </div>
                          <div className="flex justify-between font-medium">
                            <span>Total TVAC:</span>
                            <span className="text-blue-600">{formatCurrency(devis.total_tvac)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Confirm step */}
                  {step === 'confirm' && (
                    <div className="space-y-4">
                      <div className="bg-amber-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 flex-shrink-0" />
                          <div>
                            <h3 className="font-medium text-amber-900">Confirmer la conversion</h3>
                            <p className="text-sm text-amber-700 mt-1">
                              Vous allez copier {devis.lines?.length || 0} produit(s) vers l'affaire <strong>{affaireId}</strong>.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                          <span className="font-medium text-gray-900">{devis.devis_number}</span>
                        </div>
                        {devis.lines && devis.lines.length > 0 && (
                          <div className="space-y-2">
                            {devis.lines.map((line, index) => (
                              <div key={index} className="flex justify-between text-sm">
                                <span className="text-gray-600 truncate flex-1">{line.description}</span>
                                <span className="text-gray-900 ml-2">{formatCurrency(line.line_total_tvac)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Converting state */}
                  {step === 'converting' && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-4 text-gray-600">Conversion en cours...</p>
                    </div>
                  )}

                  {/* Success state */}
                  {step === 'success' && (
                    <div className="text-center py-8">
                      <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto" />
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">Conversion reussie</h3>
                      <p className="mt-2 text-gray-600">
                        Les produits du devis ont ete copies vers l'affaire {affaireId}.
                      </p>
                    </div>
                  )}

                  {/* Error state */}
                  {step === 'error' && (
                    <div className="text-center py-8">
                      <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto" />
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">Erreur</h3>
                      <p className="mt-2 text-red-600">{errorMessage}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                  {step === 'input' && (
                    <>
                      <button
                        onClick={onClose}
                        className="facturation-btn-modal-cancel"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={handleProceedToConfirm}
                        className="facturation-btn-modal-primary"
                        style={{ backgroundColor: '#3b82f6' }}
                      >
                        Continuer
                      </button>
                    </>
                  )}
                  {step === 'confirm' && (
                    <>
                      <button
                        onClick={() => setStep('input')}
                        className="facturation-btn-modal-cancel"
                      >
                        Retour
                      </button>
                      <button
                        onClick={handleConvert}
                        className="facturation-btn-modal-primary"
                        style={{ backgroundColor: '#3b82f6' }}
                      >
                        Confirmer la conversion
                      </button>
                    </>
                  )}
                  {(step === 'success' || step === 'error') && (
                    <button
                      onClick={onClose}
                      className="facturation-btn-modal-primary"
                      style={{ backgroundColor: '#3b82f6' }}
                    >
                      Fermer
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConvertDevisModal;
